import clsx from "clsx";
import { useTimezone } from "../lib/datetime";

export type PageId =
  | "dashboard"
  | "drive"
  | "pumps"
  | "lubrication"
  | "jackingForce";

interface SidebarProps {
  page: PageId;
  onChange: (p: PageId) => void;
  onOpenSettings: () => void;
  onOpenDownload: () => void;
  onOpenResync: () => void;
  onOpenDiary: () => void;
  onOpenAlerts?: () => void;
  /** Opens the pipe-schedule drawer. */
  onOpenPipeSchedule?: () => void;
  /** Mobile-only — drawer open state controlled by App.tsx. Ignored on
   *  desktop (where the sidebar is always inline). */
  mobileOpen?: boolean;
  /** Mobile-only — close handler for backdrop clicks. */
  onMobileClose?: () => void;
}

export function Sidebar({
  page,
  onChange,
  onOpenSettings,
  onOpenDownload,
  onOpenResync,
  onOpenDiary,
  onOpenAlerts,
  onOpenPipeSchedule,
  mobileOpen = false,
  onMobileClose,
}: SidebarProps) {
  const tz = useTimezone();
  return (
    <>
      {/* Backdrop: only rendered below `md`, only visible when the drawer
          is open. Tapping closes the drawer. */}
      <div
        className={clsx(
          "hidden max-md:block fixed inset-0 z-40 bg-black/35 transition-opacity",
          mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={onMobileClose}
        aria-hidden="true"
      />
      <aside
        className={clsx(
          // Desktop layout (md+): inline column, 220 px wide, static
          // border. These classes are the same as before — unchanged.
          "w-[220px] flex-shrink-0 border-r border-border1 bg-surface1 flex flex-col",
          // Mobile (< md) overrides: become a fixed-position slide-in
          // drawer. translate-x controls whether the drawer is parked
          // off-screen to the left or visible.
          "max-md:fixed max-md:inset-y-0 max-md:left-0 max-md:z-50 max-md:w-[260px] max-md:shadow-xl max-md:transition-transform max-md:duration-200 max-md:will-change-transform",
          mobileOpen ? "max-md:translate-x-0" : "max-md:-translate-x-full",
        )}
      >
        <nav className="flex-1 py-5 overflow-y-auto">
          <NavSection title="Overview">
            <NavItem
              active={page === "dashboard"}
              onClick={() => onChange("dashboard")}
              label="Dashboard"
              hint="Live TBM controls"
            />
            <NavItem
              active={page === "drive"}
              onClick={() => onChange("drive")}
              label="Drive parameters"
              hint="Plot multiple parameters"
            />
            <NavItem
              active={page === "pumps"}
              onClick={() => onChange("pumps")}
              label="Pumps"
              hint="Feed and slurry pumps"
            />
            <NavItem
              active={page === "lubrication"}
              onClick={() => onChange("lubrication")}
              label="Lubrication"
              hint="Bentonite delivery"
            />
            <NavItem
              active={page === "jackingForce"}
              onClick={() => onChange("jackingForce")}
              label="Jacking Force prediction"
              hint="Live parameter prediction"
            />
          </NavSection>

          <NavSection title="Tools">
            <NavItem
              active={false}
              onClick={() => onOpenAlerts?.()}
              label="Warnings"
              hint="Threshold alerts & email"
            />
            <NavItem
              active={false}
              onClick={onOpenDiary}
              label="Diary"
              hint="Operator log entries"
            />
            <NavItem
              active={false}
              onClick={() => onOpenPipeSchedule?.()}
              label="Pipe schedule"
              hint="Detect &amp; pin pipe changes"
            />
            <NavItem
              active={false}
              onClick={onOpenSettings}
              label="Preprocessing"
              hint="Tune filters & aggregation"
            />
            <NavItem
              active={false}
              onClick={onOpenDownload}
              label="Download"
              hint="Export CSV"
            />
            <NavItem
              active={false}
              onClick={onOpenResync}
              label="Drive setup"
              hint="Resync & set drive parameters"
            />
          </NavSection>
        </nav>

        <div className="px-5 py-4 border-t border-border1 text-[10px] text-dim tracking-widest uppercase">
          Ward &amp; Burke
          <br />
          <span className="text-muted">Tunnel Engineering</span>
          <br />
          {/* Active timezone shown so a misconfigured zone is obvious at
              a glance (rather than presenting as ticks shifted by ±1h).
              Set in the Drive setup tool. */}
          <span
            className="block mt-2 normal-case tracking-normal text-[10.5px] text-muted"
            title="Display timezone — set in Drive setup"
          >
            tz: <span className="mono-nums text-text">{tz}</span>
          </span>
        </div>
      </aside>
    </>
  );
}

function NavSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <div className="px-5 mb-2 text-[10px] font-semibold tracking-[0.22em] uppercase text-dim">
        {title}
      </div>
      <ul className="flex flex-col">{children}</ul>
    </div>
  );
}

function NavItem({
  active,
  onClick,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  hint: string;
}) {
  return (
    <li>
      <button
        onClick={onClick}
        className={clsx(
          "group relative w-full text-left pl-5 pr-3 py-2.5 flex flex-col",
          "transition-colors",
          active ? "bg-surface2" : "hover:bg-surface2/60",
        )}
      >
        <span
          className={clsx(
            "absolute left-0 top-0 bottom-0 w-[3px]",
            active ? "bg-wb" : "bg-transparent group-hover:bg-border2",
          )}
        />
        <span
          className={clsx(
            "text-[13px] font-medium",
            active ? "text-text" : "text-muted group-hover:text-text",
          )}
        >
          {label}
        </span>
        <span className="text-[11px] text-dim">{hint}</span>
      </button>
    </li>
  );
}
